import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, Check, X, UserPlus } from "lucide-react";
import {
  usePendingDualRoleRequests,
  useApproveDualRoleRequest,
  useDeclineDualRoleRequest,
} from "@/hooks/useDualRoleRequestsAdmin";
import { useStorageUrl } from "@/hooks/useStorageUrl";

function RequestRow({
  request,
}: {
  request: ReturnType<typeof usePendingDualRoleRequests>["data"] extends (infer T)[] | undefined
    ? T
    : never;
}) {
  const [confirming, setConfirming] = useState<"approve" | "decline" | null>(null);
  const approve = useApproveDualRoleRequest();
  const decline = useDeclineDualRoleRequest();
  const avatarUrl = useStorageUrl("avatars", request.profile?.avatar_url ?? null);

  return (
    <div className="flex items-center justify-between gap-4 p-4 border rounded-lg">
      <div className="flex items-center gap-3 min-w-0">
        <Avatar className="h-10 w-10">
          <AvatarImage src={avatarUrl || undefined} />
          <AvatarFallback>
            {request.profile?.full_name?.charAt(0)?.toUpperCase() || "?"}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="font-medium truncate">
            {request.profile?.full_name || "Unknown User"}
          </p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Current:</span>
            <Badge variant="secondary" className="capitalize">
              {request.profile?.role ?? "—"}
            </Badge>
            <span>→ Requesting:</span>
            <Badge variant="default" className="capitalize">
              {request.requested_role}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {new Date(request.requested_at).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {confirming === "approve" ? (
          <>
            <span className="text-xs text-muted-foreground">Approve?</span>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                await approve.mutateAsync(request.id);
                setConfirming(null);
              }}
              disabled={approve.isPending}
            >
              {approve.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Confirm"
              )}
            </Button>
          </>
        ) : confirming === "decline" ? (
          <>
            <span className="text-xs text-muted-foreground">Decline?</span>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={async () => {
                await decline.mutateAsync({ requestId: request.id });
                setConfirming(null);
              }}
              disabled={decline.isPending}
            >
              {decline.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Confirm"
              )}
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirming("decline")}
            >
              <X className="h-4 w-4 mr-1" />
              Decline
            </Button>
            <Button size="sm" onClick={() => setConfirming("approve")}>
              <Check className="h-4 w-4 mr-1" />
              Approve
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function DualRoleRequestsPanel() {
  const { data: requests, isLoading, isError } = usePendingDualRoleRequests();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="h-5 w-5" />
          Role Requests
        </CardTitle>
        <CardDescription>
          Users requesting an additional role (mentor or entrepreneur capability).
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <p className="text-sm text-destructive text-center py-8">
            Failed to load role requests. You may not have permission to view this data.
          </p>
        ) : !requests || requests.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No pending role requests.
          </p>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => (
              <RequestRow key={r.id} request={r} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
